/**
 * demo/App.jsx — Vibrotactile Design Workbench
 *
 * Imports exclusively from 'haptic-audio'. No inline DSP.
 *
 * What this file does:
 *   - Manages React state (voice selection, params, live param, play state, presets)
 *   - Creates one AudioEngine on first Play press (inside a user-gesture handler)
 *   - Delegates all audio to engine.startVoice / engine.updateVoice / engine.stopVoice
 *   - Reads engine.analyser + engine.ctx for the RAF visualization loop
 *   - Reads VoiceClass.meta for all UI labels and VoiceClass.defaultParams() for
 *     initial param values — no voice-specific logic lives here
 *
 * Slider fix:
 *   Custom <Slider> built on pointer events (not <input type="range">).
 *   Uses setPointerCapture so drag continues outside the element bounds.
 *   Reads getBoundingClientRect() on every pointermove — immune to reflows.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  AudioEngine,
  WaterFlowVoice,
  HeartbeatVoice,
  RecipeVoice,
} from 'hapticaudioengine';

import {
  BG, MONO, BANDS,
  bandEnergies,
  drawWave, drawSpectrum,
  drawParamCurves,
  initReceptorBars, updateReceptorBars,
} from './vis.js';

// ── Voice table ───────────────────────────────────────────────────────────────
// Maps the voice name used by VoiceRegistry to its class.
// All UI text comes from VoiceClass.meta — nothing is hardcoded here.
const VOICES = {
  waterFlow: WaterFlowVoice,
  heartbeat: HeartbeatVoice,
  recipe:    RecipeVoice,
};

// ── Parameter schemas ─────────────────────────────────────────────────────────
// Drives ParamEditor entirely. No rendering logic in the schema.
//
//   (no type key)        → custom Slider
//   type: 'select'       → <select>
//   type: 'toggle'       → boolean button
//   nested plain object  → labelled group
const SCHEMAS = {
  waterFlow: {
    freqBase:     { label: 'Base Freq',        min: 20,   max: 400, step: 1,     unit: 'Hz' },
    detuneSpread: { label: 'Detune Spread',    min: 0,    max: 120, step: 0.5,   unit: 'Hz' },
    lfoRateMin:   { label: 'LFO Rate (low)',   min: 0.1,  max: 30,  step: 0.1,   unit: 'Hz' },
    lfoRateMax:   { label: 'LFO Rate (high)',  min: 0.5,  max: 50,  step: 0.1,   unit: 'Hz' },
    lfoDepthMin:  { label: 'LFO Depth (low)',  min: 0,    max: 1,   step: 0.001 },
    lfoDepthMax:  { label: 'LFO Depth (high)', min: 0,    max: 1,   step: 0.001 },
    gainNormal:   { label: 'Gain (normal)',    min: 0,    max: 1,   step: 0.001 },
    gainAlert:    { label: 'Gain (alert)',     min: 0,    max: 1,   step: 0.001 },
    threshold:    { label: 'Step Threshold',   min: 0,    max: 1,   step: 0.01  },
    sigmoidK:     { label: 'Steepness k',      min: 1,    max: 60,  step: 0.5   },
    adsr: {
      attack:  { label: 'Attack',  min: 0.001, max: 3, step: 0.001, unit: 's' },
      decay:   { label: 'Decay',   min: 0.001, max: 3, step: 0.001, unit: 's' },
      sustain: { label: 'Sustain', min: 0,     max: 1, step: 0.01              },
      release: { label: 'Release', min: 0.01,  max: 5, step: 0.01,  unit: 's' },
    },
  },
  heartbeat: {
    bpmMin:       { label: 'BPM Min',          min: 10,   max: 200, step: 1,     unit: 'bpm' },
    bpmMax:       { label: 'BPM Max',          min: 10,   max: 400, step: 1,     unit: 'bpm' },
    freqStartMin: { label: 'Pitch Start (lo)', min: 20,   max: 500, step: 1,     unit: 'Hz'  },
    freqStartMax: { label: 'Pitch Start (hi)', min: 20,   max: 500, step: 1,     unit: 'Hz'  },
    freqEndMin:   { label: 'Pitch End (lo)',   min: 10,   max: 300, step: 1,     unit: 'Hz'  },
    freqEndMax:   { label: 'Pitch End (hi)',   min: 10,   max: 300, step: 1,     unit: 'Hz'  },
    punchDur:     { label: 'Punch Duration',   min: 0.02, max: 0.8, step: 0.001, unit: 's'  },
    peakGain:     { label: 'Peak Gain',        min: 0,    max: 1,   step: 0.001             },
    lubDubGap:    { label: 'Lub-Dub Gap',      min: 0.02, max: 0.5, step: 0.001, unit: 's' },
  },
  recipe: {
    oscCount:     { label: 'Osc Count',        min: 1,   max: 8,   step: 1    },
    oscType:      { label: 'Osc Type',         type: 'select', options: ['sine','sawtooth','square','triangle'] },
    baseFreq:     { label: 'Base Freq',        min: 10,  max: 600, step: 1,   unit: 'Hz' },
    detuneSpread: { label: 'Detune Spread',    min: 0,   max: 300, step: 1,   unit: 'Hz' },
    hasLFO:       { label: 'LFO',              type: 'toggle' },
    lfoType:      { label: 'LFO Type',         type: 'select', options: ['sine','triangle','square','sawtooth'] },
    lfoRateMin:   { label: 'LFO Rate (low)',   min: 0.1, max: 30,  step: 0.1, unit: 'Hz' },
    lfoRateMax:   { label: 'LFO Rate (high)',  min: 0.1, max: 50,  step: 0.1, unit: 'Hz' },
    lfoDepthMin:  { label: 'LFO Depth (low)',  min: 0,   max: 1,   step: 0.001 },
    lfoDepthMax:  { label: 'LFO Depth (high)', min: 0,   max: 1,   step: 0.001 },
    gainMode:     { label: 'Gain Mode',        type: 'select', options: ['sigmoid','invSigmoid','linear','constant'] },
    paramInverse: { label: 'Invert Param',     type: 'toggle' },
    gainLow:      { label: 'Gain (low)',       min: 0,   max: 1,   step: 0.001 },
    gainHigh:     { label: 'Gain (high)',      min: 0,   max: 1,   step: 0.001 },
    threshold:    { label: 'Step Threshold',   min: 0,   max: 1,   step: 0.01  },
    sigmoidK:     { label: 'Steepness k',      min: 1,   max: 60,  step: 0.5   },
    adsr: {
      attack:  { label: 'Attack',  min: 0.001, max: 3, step: 0.001, unit: 's' },
      decay:   { label: 'Decay',   min: 0.001, max: 3, step: 0.001, unit: 's' },
      sustain: { label: 'Sustain', min: 0,     max: 1, step: 0.01              },
      release: { label: 'Release', min: 0.01,  max: 5, step: 0.01,  unit: 's' },
    },
  },
};

// ── Slider ────────────────────────────────────────────────────────────────────
//
// Why not <input type="range">?
// React's synthetic event system and browser-native range inputs interact
// badly: onChange fires on click but drag events get dropped if the pointer
// moves outside the element before mouseup. The real fix is pointer capture.
//
// This component:
//   1. Attaches onPointerDown to the track div (not the thumb)
//   2. Calls setPointerCapture — browser delivers ALL subsequent pointer events
//      to this element regardless of where the pointer goes
//   3. Computes the new value from getBoundingClientRect() on every move
//   4. Releases capture on pointerup
//
// The thumb is pointer-events:none so it never intercepts events from the track.

function Slider({ min, max, step, value, onChange, accent = '#3b82f6' }) {
  const trackRef = useRef(null);
  const active   = useRef(false);

  const fromEvent = useCallback((e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const f    = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const raw  = min + f * (max - min);
    const snapped = Math.round((raw - min) / step) * step + min;
    return Math.max(min, Math.min(max, snapped));
  }, [min, max, step]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    active.current = true;
    trackRef.current.setPointerCapture(e.pointerId);
    onChange(fromEvent(e));
  }, [onChange, fromEvent]);

  const onPointerMove = useCallback((e) => {
    if (!active.current) return;
    onChange(fromEvent(e));
  }, [onChange, fromEvent]);

  const onPointerUp = useCallback((e) => {
    active.current = false;
    trackRef.current.releasePointerCapture(e.pointerId);
  }, []);

  const pct = `${((value - min) / (max - min)) * 100}%`;

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: 'relative', height: 20, cursor: 'pointer',
        display: 'flex', alignItems: 'center',
        userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none',
      }}
    >
      {/* track background */}
      <div style={{ position: 'absolute', left: 0, right: 0, height: 3,
                    borderRadius: 2, background: 'rgba(255,255,255,0.08)' }} />
      {/* filled portion */}
      <div style={{ position: 'absolute', left: 0, width: pct, height: 3,
                    borderRadius: 2, background: accent }} />
      {/* thumb — pointer-events:none so the track div always receives events */}
      <div style={{
        position: 'absolute', left: `calc(${pct} - 7px)`,
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        boxShadow: `0 0 0 2px ${accent}, 0 2px 6px rgba(0,0,0,0.5)`,
        pointerEvents: 'none',
      }} />
    </div>
  );
}

// ── ParamEditor ───────────────────────────────────────────────────────────────

function ParamEditor({ voiceId, params, onChange }) {
  const schema = SCHEMAS[voiceId];
  if (!schema || !params) return null;

  // Set a value at a dotted path — supports one level of nesting (e.g. "adsr.attack")
  const set = (path, val) => {
    const [a, b] = path.split('.');
    onChange(!b ? { ...params, [a]: val }
                : { ...params, [a]: { ...params[a], [b]: val } });
  };

  const fmt = (v, step) => typeof v === 'number'
    ? v.toFixed(step < 0.01 ? 3 : step < 1 ? 2 : 0) : String(v ?? '');

  const Field = ({ path, def, value }) => {
    if (def.type === 'select') return (
      <div style={{ marginBottom: 9 }}>
        <div style={{ fontSize: 9, color: '#475569', fontFamily: MONO, marginBottom: 4 }}>{def.label}</div>
        <select value={value ?? def.options[0]} onChange={e => set(path, e.target.value)}
          style={{ width: '100%', background: '#0d1e33', border: '1px solid rgba(255,255,255,0.08)',
                   color: '#93c5fd', fontSize: 10, fontFamily: MONO, padding: '4px 7px',
                   borderRadius: 3, cursor: 'pointer', outline: 'none' }}>
          {def.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );

    if (def.type === 'toggle') return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
        <span style={{ fontSize: 9, color: '#475569', fontFamily: MONO }}>{def.label}</span>
        <button onClick={() => set(path, !value)} style={{
          padding: '3px 11px', cursor: 'pointer', fontFamily: MONO, fontSize: 9, borderRadius: 3,
          background: value ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${value ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.08)'}`,
          color: value ? '#34d399' : '#475569',
        }}>{value ? 'ON' : 'OFF'}</button>
      </div>
    );

    return (
      <div style={{ marginBottom: 9 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 9, color: '#475569', fontFamily: MONO }}>{def.label}</span>
          <span style={{ fontSize: 10, color: '#93c5fd', fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
            {fmt(value, def.step)}{def.unit ? ' ' + def.unit : ''}
          </span>
        </div>
        <Slider min={def.min} max={def.max} step={def.step}
          value={value ?? def.min} onChange={v => set(path, v)} />
      </div>
    );
  };

  return (
    <div style={{ padding: '8px 13px 14px' }}>
      {Object.entries(schema).map(([key, def]) =>
        def.label
          ? <Field key={key} path={key} def={def} value={params[key]} />
          : (
            <div key={key}>
              <div style={{ fontSize: 9, color: '#1e3a5f', fontFamily: MONO,
                            letterSpacing: '0.1em', marginTop: 12, marginBottom: 7 }}>
                {key.toUpperCase()}
              </div>
              {Object.entries(def).map(([sk, sd]) =>
                <Field key={sk} path={`${key}.${sk}`} def={sd} value={params[key]?.[sk]} />
              )}
            </div>
          )
      )}
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const firstVoiceId = 'waterFlow';
  const firstClass   = VOICES[firstVoiceId];

  const [voiceId,    setVoiceId]    = useState(firstVoiceId);
  const [params,     setParams]     = useState(() => firstClass.defaultParams());
  const [liveParam,  setLiveParam]  = useState(firstClass.meta.liveDefault);
  const [playing,    setPlaying]    = useState(false);
  const [presets,    setPresets]    = useState({ waterFlow: [], heartbeat: [], recipe: [] });
  const [presetName, setPresetName] = useState('');
  const [showCode,   setShowCode]   = useState(false);

  // Single AudioEngine instance — created inside Play handler (user gesture)
  const engineRef = useRef(null);

  // DOM refs for D3 / canvas
  const waveRef     = useRef(null);
  const specRef     = useRef(null);
  const curveSvgRef = useRef(null);
  const receptorRef = useRef(null);
  const vizColRef   = useRef(null);

  // Stable mutable refs — read inside RAF without stale closures
  const voiceIdRef = useRef(voiceId);
  const paramsRef  = useRef(params);
  const liveRef    = useRef(liveParam);
  const playingRef = useRef(playing);
  const rafRef     = useRef(null);
  const restartRef = useRef(null);
  const frameRef   = useRef(0);

  useEffect(() => { voiceIdRef.current = voiceId;   }, [voiceId]);
  useEffect(() => { paramsRef.current  = params;    }, [params]);
  useEffect(() => { liveRef.current    = liveParam; }, [liveParam]);
  useEffect(() => { playingRef.current = playing;   }, [playing]);

  // ── Audio helpers ─────────────────────────────────────────────────────────

  // Lazily create the engine — only on first Play press (user gesture required)
  const getEngine = useCallback(() => {
    if (!engineRef.current) {
      engineRef.current = new AudioEngine({ masterVolume: 0.75 });
    }
    return engineRef.current;
  }, []);

  const _startVoice = useCallback((vId, p, lv) => {
    const engine = getEngine();
    // resume() must be called every time — iOS Safari suspends AudioContext
    // on every page-visibility change, not just the first interaction
    engine.resume();
    // startVoice handles stopping whatever was playing first (via VoiceRegistry)
    engine.startVoice(vId, p, lv);
  }, [getEngine]);

  const handlePlay = useCallback(() => {
    _startVoice(voiceIdRef.current, paramsRef.current, liveRef.current);
    setPlaying(true);
  }, [_startVoice]);

  const handleStop = useCallback(() => {
    engineRef.current?.stopVoice();
    setPlaying(false);
  }, []);

  // ── Voice switching ───────────────────────────────────────────────────────

  const handleVoiceChange = useCallback((newId) => {
    handleStop();
    const VC = VOICES[newId];
    setVoiceId(newId);
    setParams(VC.defaultParams());
    setLiveParam(VC.meta.liveDefault);
    setShowCode(false);
  }, [handleStop]);

  // ── Param changes — debounced restart ────────────────────────────────────

  const handleParamChange = useCallback((newParams) => {
    setParams(newParams);
    clearTimeout(restartRef.current);
    restartRef.current = setTimeout(() => {
      if (playingRef.current) {
        _startVoice(voiceIdRef.current, newParams, liveRef.current);
      }
    }, 100);
  }, [_startVoice]);

  // ── Live param — direct voice update, no restart ──────────────────────────

  const handleLiveParam = useCallback((val) => {
    setLiveParam(val);
    engineRef.current?.updateVoice(val);
  }, []);

  // ── D3 init ───────────────────────────────────────────────────────────────

  const reinitD3 = useCallback(() => {
    initReceptorBars(receptorRef.current);
  }, []);

  useEffect(() => { reinitD3(); }, [reinitD3]);
  useEffect(() => { reinitD3(); }, [voiceId, reinitD3]);

  useEffect(() => {
    if (!vizColRef.current) return;
    const ro = new ResizeObserver(reinitD3);
    ro.observe(vizColRef.current);
    return () => ro.disconnect();
  }, [reinitD3]);

  // Param curves — redraw whenever voice or params change
  const voiceClass = VOICES[voiceId];
  useEffect(() => {
    drawParamCurves(curveSvgRef.current, voiceClass.meta, params, liveParam);
  }, [voiceId, params, liveParam, voiceClass]);

  // ── RAF draw loop ─────────────────────────────────────────────────────────

  useEffect(() => {
    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      const n   = ++frameRef.current;
      const eng = engineRef.current;

      // engine.analyser is the AnalyserNode wired between masterGain and destination
      const analyser = eng?.analyser ?? null;
      const actx     = eng?.ctx      ?? null;

      // Oscillator frequency markers for the spectrum overlay
      const p = paramsRef.current;
      let oscFreqs = null;
      const vid = voiceIdRef.current;
      if (vid === 'waterFlow' && p?.freqBase != null) {
        oscFreqs = [-1.5, -0.5, 0.5, 1.5].map(m => p.freqBase + m * p.detuneSpread);
      } else if (vid === 'recipe' && p?.baseFreq != null) {
        const nn   = Math.max(1, Math.round(p.oscCount ?? 2));
        const step = nn > 1 ? p.detuneSpread / (nn - 1) : 0;
        oscFreqs   = Array.from({ length: nn }, (_, i) =>
          p.baseFreq + (-p.detuneSpread / 2 + i * step));
      }

      drawWave(waveRef.current, analyser);
      drawSpectrum(specRef.current, analyser, actx, oscFreqs);
      if (n % 4 === 0) {
        updateReceptorBars(receptorRef.current, bandEnergies(analyser, actx));
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // runs once — stable via refs

  // Cleanup on unmount
  useEffect(() => () => {
    clearTimeout(restartRef.current);
    engineRef.current?.dispose();
    engineRef.current = null;
  }, []);

  // ── Presets ───────────────────────────────────────────────────────────────

  const savePreset = () => {
    const name = presetName.trim() || `Preset ${presets[voiceId].length + 1}`;
    setPresets(prev => ({ ...prev, [voiceId]: [...prev[voiceId], { name, params: { ...params }, liveParam }] }));
    setPresetName('');
  };
  const loadPreset = (p) => { handleParamChange(p.params); handleLiveParam(p.liveParam); };
  const delPreset  = (i) => setPresets(prev => ({
    ...prev, [voiceId]: prev[voiceId].filter((_, j) => j !== i),
  }));

  // ── Code export ───────────────────────────────────────────────────────────

  const exportCode = () => {
    const cfgKey = voiceId;
    const lines  = [
      `import { AudioEngine } from 'haptic-audio';`,
      ``,
      `const engine = new AudioEngine({`,
      `  ${cfgKey}: ${JSON.stringify(params, null, 4).replace(/\n/g, '\n  ')},`,
      `});`,
      ``,
      `// ${voiceClass.meta.liveLabel}: ${voiceClass.meta.liveDesc}`,
      `engine.startVoice('${voiceId}', engine._cfg.${cfgKey}, ${liveParam.toFixed(3)});`,
      `engine.updateVoice(newValue);`,
      `engine.stopVoice();`,
    ];
    return lines.join('\n');
  };

  // ── Shared style tokens ───────────────────────────────────────────────────

  const card = { background: '#070f1d', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 4 };
  const SL   = ({ children }) => (
    <div style={{ fontSize: 9, color: '#1e3a5f', fontFamily: MONO,
                  letterSpacing: '0.12em', padding: '8px 12px 4px' }}>
      {children}
    </div>
  );
  const meta = voiceClass.meta;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ background: BG, minHeight: '100vh', color: '#cbd5e1',
                  fontFamily: MONO, padding: '16px 14px', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 11,
                    marginBottom: 14, display: 'flex', alignItems: 'baseline',
                    flexWrap: 'wrap', gap: '8px 18px' }}>
        <span style={{ fontSize: 11, letterSpacing: '0.2em', color: '#e2e8f0' }}>
          VIBROTACTILE DESIGN WORKBENCH
        </span>
        <span style={{ fontSize: 9, letterSpacing: '0.12em', color: '#1e3a5f' }}>
          haptic-audio · Web Audio API · D3.js
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── LEFT: controls ─────────────────────────────────────────────── */}
        <div style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Voice selector tabs */}
          <div style={{ ...card, overflow: 'hidden' }}>
            <SL>VOICE</SL>
            <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {Object.entries(VOICES).map(([id, VC], i, arr) => (
                <button key={id} onClick={() => handleVoiceChange(id)} style={{
                  flex: 1, padding: '9px 0', cursor: 'pointer',
                  background: voiceId === id ? 'rgba(59,130,246,0.14)' : 'transparent',
                  color: voiceId === id ? '#93c5fd' : '#334155',
                  border: 'none',
                  borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  fontSize: 9, letterSpacing: '0.09em', fontFamily: MONO,
                }}>
                  {VC.meta.label.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Play / Stop */}
          <button onClick={playing ? handleStop : handlePlay} style={{
            ...card, cursor: 'pointer', width: '100%',
            background: playing ? 'rgba(248,113,113,0.08)' : 'rgba(52,211,153,0.08)',
            border: `1px solid ${playing ? 'rgba(248,113,113,0.35)' : 'rgba(52,211,153,0.35)'}`,
            color: playing ? '#f87171' : '#34d399',
            padding: '11px 0', fontSize: 11, letterSpacing: '0.16em', fontFamily: MONO,
          }}>
            {playing ? '■  STOP' : '▶  PLAY'}
          </button>

          {/* Live parameter slider */}
          <div style={card}>
            <SL>{meta.liveLabel.toUpperCase()}</SL>
            <div style={{ padding: '4px 13px 13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 9, color: '#334155' }}>{meta.liveDesc}</span>
                <span style={{ fontSize: 13, color: '#34d399', fontWeight: 'bold',
                               fontVariantNumeric: 'tabular-nums' }}>
                  {liveParam.toFixed(3)}
                </span>
              </div>
              <Slider min={0} max={1} step={0.005} value={liveParam}
                onChange={handleLiveParam} accent='#10b981' />
            </div>
          </div>

          {/* Presets */}
          <div style={card}>
            <SL>PRESETS</SL>
            <div style={{ padding: '4px 11px 10px' }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input value={presetName} onChange={e => setPresetName(e.target.value)}
                  placeholder="name…"
                  style={{ flex: 1, background: 'rgba(255,255,255,0.03)',
                           border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3,
                           color: '#cbd5e1', fontSize: 9, fontFamily: MONO,
                           padding: '4px 6px', outline: 'none' }} />
                <button onClick={savePreset} style={{
                  background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
                  color: '#93c5fd', borderRadius: 3, padding: '4px 9px',
                  cursor: 'pointer', fontSize: 9, fontFamily: MONO,
                }}>SAVE</button>
              </div>
              {presets[voiceId].length === 0
                ? <div style={{ fontSize: 9, color: '#1e3a5f', textAlign: 'center', padding: '4px 0' }}>
                    no presets
                  </div>
                : presets[voiceId].map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    <button onClick={() => loadPreset(p)} style={{
                      flex: 1, textAlign: 'left', background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3,
                      color: '#94a3b8', padding: '5px 8px', cursor: 'pointer',
                      fontFamily: MONO, fontSize: 9,
                    }}>
                      {p.name}
                      <span style={{ float: 'right', color: '#334155' }}>
                        {p.liveParam.toFixed(2)}
                      </span>
                    </button>
                    <button onClick={() => delPreset(i)} style={{
                      background: 'transparent', border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 3, color: '#334155', padding: '0 8px',
                      cursor: 'pointer', fontFamily: MONO, fontSize: 13,
                    }}>×</button>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Code export */}
          <div style={{ ...card, overflow: 'hidden' }}>
            <button onClick={() => setShowCode(v => !v)} style={{
              width: '100%', background: 'transparent', border: 'none',
              padding: '9px 12px', cursor: 'pointer', fontFamily: MONO,
              fontSize: 9, letterSpacing: '0.12em', textAlign: 'left',
              color: showCode ? '#93c5fd' : '#334155',
            }}>
              {showCode ? '▼' : '▶'}&nbsp;&nbsp;EXPORT CODE
            </button>
            {showCode && (
              <pre style={{ margin: 0, padding: '0 11px 11px', fontSize: 8.5,
                            color: '#475569', overflowX: 'auto',
                            borderTop: '1px solid rgba(255,255,255,0.05)', lineHeight: 1.7 }}>
                {exportCode()}
              </pre>
            )}
          </div>

        </div>

        {/* ── CENTER: visualizations ──────────────────────────────────────── */}
        <div ref={vizColRef} style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 10 }}>

          <div style={card}>
            {/* vh heights → fixed viewport proportion at any screen size */}
            <canvas ref={waveRef}
              style={{ display: 'block', width: '100%', height: '15vh', minHeight: 80, maxHeight: 130 }} />
          </div>

          <div style={card}>
            <canvas ref={specRef}
              style={{ display: 'block', width: '100%', height: '28vh', minHeight: 160, maxHeight: 340 }} />
          </div>

          {/* Band legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', padding: '0 2px' }}>
            {BANDS.map(b => (
              <span key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 8, height: 8, display: 'inline-block', borderRadius: 2,
                               background: b.color + '18', border: `1px solid ${b.color}` }} />
                <span style={{ fontSize: 9, color: '#334155' }}>
                  {b.label} · {b.fMin}–{b.fMax} Hz · {b.sub}
                </span>
              </span>
            ))}
            {voiceId !== 'heartbeat' && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 12, display: 'inline-block',
                               borderTop: '1px dashed rgba(251,191,36,0.45)' }} />
                <span style={{ fontSize: 9, color: '#334155' }}>osc fundamentals</span>
              </span>
            )}
          </div>

        </div>

        {/* ── RIGHT: analysis + param editor ─────────────────────────────── */}
        <div style={{ width: 262, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Param mapping curves (D3 SVG) */}
          <div style={{ ...card, overflow: 'hidden' }}>
            <svg ref={curveSvgRef} style={{ display: 'block', width: '100%' }} />
          </div>

          {/* Receptor activation bars (D3 SVG) */}
          <div style={{ ...card, overflow: 'hidden' }}>
            <svg ref={receptorRef} style={{ display: 'block', width: '100%' }} />
          </div>

          {/* Parameter editor */}
          <div style={card}>
            <SL>PARAMETERS — {meta.label.toUpperCase()}</SL>
            <div style={{
              borderTop: '1px solid rgba(255,255,255,0.05)',
              maxHeight: '38vh', overflowY: 'auto',
            }}>
              <ParamEditor voiceId={voiceId} params={params} onChange={handleParamChange} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}